interface ModuleActionsUrls {
  install: string;
  uninstall: string;
  delete: string;
  enable: string;
  disable: string;
  reset: string;
  upgrade: string;
  upload?: string;
  configure?: string;
}

export const handleModuleAction = {
  type: 'function',
  required: true,
}

export type Action =
  | 'disable'
  | 'disable_mobile'
  | 'enable'
  | 'enable_mobile'
  | 'install'
  | 'reset'
  | 'uninstall'
  | 'upgrade'
  | 'upload'

/**
 * Builds the configuration URL for a module
 * Same logic as in oldModuleAction to extract the base URL and token
 * @param url The current action URL
 * @param moduleName The module name
 * @returns The module configuration URL
 */
function buildConfigureUrl(url: string, moduleName: string): string {
  let currentUrlSplit

  if (url.indexOf('?controller=apiSecurityPsMbo') !== -1) {
    currentUrlSplit = url.split('?controller=apiSecurityPsMbo')
  } else if (url.indexOf('?controller=ApiSecurityPsMbo') !== -1) {
    currentUrlSplit = url.split('?controller=ApiSecurityPsMbo')
  } else {
    // Otherwise, extract the part before /action/[action]/module
    const actionMatch = url.match(/^(.+?)\/action\/[^\/]+\//)
    if (actionMatch) {
      currentUrlSplit = [actionMatch[1], '']
    } else {
      currentUrlSplit = url.split('/modules/mbo/me')
    }
  }

  let basePath = currentUrlSplit[0].replace(/\/$/, '')

  // Remove trailing /improve/modules/manage if it's already present to avoid duplication
  if (basePath.endsWith('/improve/modules/manage')) {
    basePath = basePath.slice(0, -'/improve/modules/manage'.length)
  }

  const baseUrl = basePath + '/improve/modules/manage/action'

  // Extract the token from the URL
  const tokenMatch = url.match(/[?&]_token=([^&]+)/)
  const token = tokenMatch ? tokenMatch[1] : ''

  return `${baseUrl}/configure/${moduleName}${token ? `?_token=${token}` : ''}`
}


function addUploadUrlToActionsUrl(actions_url: ModuleActionsUrls): ModuleActionsUrls {
  if (!actions_url.upload) {
    return {
      upload: actions_url.upgrade.replace('/upgrade/', '/upload/'),
      ...actions_url
    }
  }
  return actions_url
}

function getActionUrl(
  action: Action,
  moduleName: string,
  actions_url: ModuleActionsUrls,
  moduleId?: string,
  sourceUrl?: string,
): string {

  const enrichedActionsUrl = addUploadUrlToActionsUrl(actions_url)

  let url = enrichedActionsUrl[action].replace(':module', moduleName)

  if (moduleId) {
    url = url.replace('_module_id_', moduleId)
  }

  if (sourceUrl) {
    url = url.replace('_download_url_', encodeURIComponent(sourceUrl))
  } else {
    url = url.replace('&source=_download_url_', '')
  }

  return url
}

export const moduleAction = async (
  action: Action,
  moduleName: string,
  actions_url: ModuleActionsUrls,
  moduleId?: string,
  sourceUrl?: string,
) => {

  let url = getActionUrl(action, moduleName, actions_url, moduleId, sourceUrl)

  const requestParams: RequestInit = {
    method: 'POST',
    mode: 'same-origin',
    cache: 'no-cache',
    redirect: 'error',
  }

  const request = await fetch(url, requestParams)
  const res = await request.json()

  if (res?.[moduleName]?.status) {
    if (res[moduleName].is_configurable && !res[moduleName].config_url) {
      res[moduleName].config_url = buildConfigureUrl(url, moduleName)
    }
    return res[moduleName]
  }

  throw new Error(res?.[moduleName]?.msg)
}

// Before ps.9.0.0
export const oldModuleAction = async (
  action: Action,
  moduleName: string,
  moduleId: number,
  token: string,
  mboRefreshUrl: string,
  reload = false,
) => {
  let currentUrlSplit

  if (mboRefreshUrl.indexOf('?controller=apiSecurityPsMbo') !== -1)
    currentUrlSplit = mboRefreshUrl.split('?controller=apiSecurityPsMbo')
  else {
    if (mboRefreshUrl.indexOf('?controller=ApiSecurityPsMbo') !== -1)
      currentUrlSplit = mboRefreshUrl.split('?controller=ApiSecurityPsMbo')
    else currentUrlSplit = mboRefreshUrl.split('/modules/mbo/me')
  }

  const baseUrl =
    currentUrlSplit[0].replace(/\/$/, '') + '/improve/modules/manage/action'

  const url = `${baseUrl}/${action}/${moduleName}?_token=${token}&module_id=${moduleId}`
  const configUrl = `${baseUrl}/configure/${moduleName}?_token=${token}`

  const requestParams: RequestInit = {
    method: 'POST',
    mode: 'same-origin',
    cache: 'no-cache',
    redirect: 'error',
  }

  if (action === 'install') {
    const data = new FormData()
    data.append('actionParams[confirmPrestaTrust]', '1')
    requestParams.body = data
  }

  const request = await fetch(url, requestParams)

  const res = await request.json()

  if (reload) document.location.reload()

  if (res?.[moduleName]?.status) {
    if (res[moduleName].is_configurable) {
      res[moduleName].config_url = configUrl
    }
    return res[moduleName]
  }

  throw new Error(res?.[moduleName]?.msg)
}

export const requestFromCdc = async (url, requestParams) => {
  const request = await fetch(url, requestParams)

  return await request.json()
}
